import { useNavigate } from "@tanstack/react-router";
import { CalendarDays, MapPin, Search } from "lucide-react";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateField } from "@/components/site/DateField";
import { MAIN_LOCATION } from "@/data/cars";
import { cn } from "@/lib/utils";

const today = () => new Date().toISOString().slice(0, 10);

export function SearchWidget({
  className,
  defaultLocation = "All Location",
}: {
  className?: string;
  defaultLocation?: string;
}) {
  const navigate = useNavigate();
  const [location, setLocation] = useState(defaultLocation);
  const [pickup, setPickup] = useState(today());
  const [ret, setRet] = useState("");

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const search: { location?: string; pickup?: string; return?: string } = {};
    if (location !== "All Location") search.location = location;
    if (pickup) search.pickup = pickup;
    if (ret) search.return = ret;
    navigate({ to: "/rentals", search });
  };


  return (
    <form
      onSubmit={onSubmit}
      className={cn(
        "grid gap-3 rounded-2xl border border-border bg-card p-4 shadow-lift md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-end",
        className,
      )}
    >
      <div className="min-w-0 space-y-1.5">
        <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <MapPin className="size-3.5 text-gold" /> Pickup Location
        </Label>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger aria-label="Pickup location" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All Location">All Location</SelectItem>
            <SelectItem value="Anywhere">Anywhere</SelectItem>
            <SelectItem value={MAIN_LOCATION}>{MAIN_LOCATION}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="min-w-0 space-y-1.5">
        <Label
          htmlFor="pickup-date"
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          <CalendarDays className="size-3.5 text-gold" /> Pickup Date
        </Label>
        <DateField
          id="pickup-date"
          ariaLabel="Pickup date"
          placeholder="Add a date"
          value={pickup}
          min={today()}
          onChange={(next) => {
            setPickup(next);
            if (ret && new Date(ret) <= new Date(next)) setRet("");
          }}
        />
      </div>

      <div className="min-w-0 space-y-1.5">
        <Label
          htmlFor="return-date"
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          <CalendarDays className="size-3.5 text-gold" /> Return Date
        </Label>
        <DateField
          id="return-date"
          ariaLabel="Return date"
          placeholder="Add a date"
          value={ret}
          min={pickup || today()}
          onChange={setRet}
        />
      </div>



      <Button type="submit" variant="gold" size="lg" className="w-full md:w-auto">
        <Search className="size-4" /> Search
      </Button>
    </form>
  );
}
